// @flow
import api from 'common/services/api';
import type { FetchCampaignOptions, NewCampaignData } from 'common/modules/campaign/domain/Campaign';
import Campaign, { InventoryCampaign } from 'common/modules/campaign/domain/Campaign';
import CampaignListing from 'common/modules/campaign/domain/CampaignListing';
import CampaignMetrics from 'common/modules/campaign/domain/CampaignMetrics';
import Radio from 'common/domain/radio';
import FileTooLargeError from 'common/domain/FileTooLargeError';
import AgeRangeReachesAPIHelper from 'common/helpers/AgeRangeReachesAPIHelper';
import CampaignDashboardListing from 'common/modules/campaign/domain/CampaignDashboardListing';
import DateRange from 'common/domain/DateRange';
import RadioUser from 'statsradio/domain/RadioUser';
import ExternalCampaign from '../domain/externalCampaign';
import { CampaignInFolloStatus } from '../domain/CampaignInFolloStatus';
import { Contract } from '../../sales/domain/contract';

export const MAX_AUDIO_FILE_SIZE_IN_MB = 5;
const BYTES_IN_A_MEGABYTE = 1000000;

class CampaignAPI {
  buildHeaders = (token: string) => ({ Authorization: 'Bearer ' + token });

  async listCampaignsForInventoryManagement(token: string, callSign: string): Promise<InventoryCampaign[]> {
    const url = `/statsradio/${callSign}/inventory/campaigns`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    return data.map(unparsed => InventoryCampaign.parseForInventory(unparsed));
  }

  async listCampaigns(token: string, callSign: string, params: FetchCampaignOptions): Promise<CampaignListing[]> {
    const url = `/statsradio/${callSign}/campaigns`;

    const options = {
      searchText: params.searchText,
      sortBy: params.sortBy,
      page: params.currentPage,
      limit: params.limit,
      filterBy: params.filterBy,
    };

    const { data } = await api.get(url, options, { headers: this.buildHeaders(token) });

    return data.items.map(unparsed => CampaignListing.parse(unparsed));
  }

  async listCampaignsForProduction(token: string, callSign: string, params: FetchCampaignOptions): Promise<CampaignListing[]> {
    const url = `/statsradio/${callSign}/production/campaigns`;

    const options = {
      searchText: params.searchText,
      sortBy: params.sortBy,
      page: params.currentPage,
      limit: params.limit,
      filterBy: params.filterBy,
    };

    const { data } = await api.get(url, options, { headers: this.buildHeaders(token) });

    return data.items.map(unparsed => CampaignListing.parse(unparsed));
  }

  async fetchCampaign(token: string, callSign: string, reference: string) {
    const url = `/statsradio/${callSign}/campaigns/${reference}`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Campaign.parse(json);
  }

  async fetchCampaignForProduction(token: string, callSign: string, reference: string) {
    const url = `/statsradio/${callSign}/production/campaigns/${reference}`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    return Campaign.parse(data);
  }

  async editCampaignMessage(token: string, callSign: string, reference: string, message: string) {
    const url = `/statsradio/${callSign}/campaigns/${reference}/production/message`;

    const body = { message };

    const { data } = await api.patch(url, body, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Campaign.parse(json);
  }

  async uploadAudioFileMessage(token: string, callSign: string, campaignReference: string, audioFile: File): Promise<Campaign> {
    if (audioFile.size >= MAX_AUDIO_FILE_SIZE_IN_MB * BYTES_IN_A_MEGABYTE) {
      throw new FileTooLargeError();
    }

    const url = `/statsradio/${callSign}/campaigns/${campaignReference}/production/upload-audio-file`;

    const formData = new FormData();
    formData.append('audioFile', audioFile);

    const headers = {
      ...this.buildHeaders(token),
      'Content-Type': 'multipart/form-data',
    };

    const { data } = await api.post(url, formData, { headers: headers });

    const json = this.assembleFlattenedData(data);

    return Campaign.parse(json);
  }

  async removeAudioFileMessage(token: string, callSign: string, campaignReference: string): Promise<Campaign> {
    const url = `/statsradio/${callSign}/campaigns/${campaignReference}/production/remove-audio-file`;

    const { data } = await api.patch(url, {}, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Campaign.parse(json);
  }

  async fetchCampaignMetrics(token: string, radio: Radio, campaignReference: string) {
    const url = `/statsradio/${radio.callSign}/campaigns/${campaignReference}/metrics`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    const json = {
      ...data,
      ageGroup: AgeRangeReachesAPIHelper.flatToJson(data.ageGroup, data.ageGroupMetadata),
    };

    return CampaignMetrics.parse(json);
  }

  async listExternalCampaigns(token: string, callSign: string) {
    const url = `/external/campaigns/${callSign}`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    return data.map(v => ExternalCampaign.parse(v));
  }

  async listRecentlyLinkedCampaigns(token: string, callSign: string): Promise<CampaignDashboardListing[]> {
    const url = `statsradio/${callSign}/campaigns/recently-linked`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    return data.items.map(CampaignDashboardListing.parse);
  }

  async linkCampaigns(token: string, callSign: string, reference: string, externalReferences: string[]) {
    const url = `/statsradio/${callSign}/campaigns/${reference}/link-all`;

    await api.post(url, externalReferences, { headers: this.buildHeaders(token) });
  }

  async deleteCampaign(token: string, callSign: string, reference: string) {
    const url = `/statsradio/${callSign}/campaigns/${reference}`;

    await api.del(url, { headers: this.buildHeaders(token) });
  }


  async completeCampaign(token: string, callSign: string, reference: string, notifyAdvertiser?: boolean) {
    const url = `/statsradio/${callSign}/campaigns/${reference}/complete`;
    const payload = { notifyAdvertiser };
    const { data } = await api.post(url, payload, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);
    return Campaign.parse(json);
  }

  async createNewCampaign(token: string, callSign: string, user: RadioUser, newCampaignData: NewCampaignData, contract: Contract) {
    const campaignJson = Campaign.newCampaignDataAsJson(newCampaignData);
    const contractJson = contract.toJSON();

    const body = {
      ...campaignJson,
      totalCosts: contract.getTotalCost(),
      spots: contract.getTotalSpots(),
      contract: contractJson,
    };

    const url = `/statsradio/${callSign}/campaigns/`;
    const { data } = await api.post(url, body, { headers: this.buildHeaders(token) });

    const json = this.assembleFlattenedData(data);

    return Campaign.parse(json);
  }

  async createDemoCampaign(token: string, callSign: string, campaignName: string, campaignDates: DateRange) {
    const body = { name: campaignName, dateRange: campaignDates.toISO() };
    const url = `/statsradio/${callSign}/campaigns/create-demo-campaign`;
    await api.post(url, body, { headers: this.buildHeaders(token) });
  }

  async activateInFollo(token: string, callSign: string, reference: string) {
    const url = `/statsradio/${callSign}/campaigns/${reference}/activate-follo`;

    await api.post(url, {}, { headers: this.buildHeaders(token) });
  }

  async verifyIfCampaignIsActivatedInFollo(token: string, callSign: string, reference: string): Promise<CampaignInFolloStatus> {
    const url = `/statsradio/${callSign}/campaigns/${reference}/verify-campaign-in-follo`;

    const { data } = await api.get(url, {}, { headers: this.buildHeaders(token) });

    return data;
  }

  assembleFlattenedData(data: Object) {
    if (!data.proposition) {
      return data;
    }

    return {
      ...data,
      proposition: {
        ...data.proposition,
        estimation: {
          ...data.proposition.estimation,
          ageGroup: AgeRangeReachesAPIHelper.flatToJson(data.proposition.estimation.ageGroup, data.proposition.estimation.ageGroupMetadata),
        },
      },
    };
  }

}

export default new CampaignAPI();



// WEBPACK FOOTER //
// ./src/statsradio/modules/campaigns/services/CampaignAPI.js